import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EditorState, ViewMode, DetectedFormat } from '../types';
import { loadSource, saveSource } from './storage';
import { log } from '../lib/logger';

const DEFAULT_SOURCE = `# Welcome to html2html

Type your **Markdown** or HTML here and see it rendered live!

## Features

- Live sync preview
- Markdown & HTML support
- Multiple view modes
- Auto-save to localStorage

\`\`\`js
const greeting = 'Hello, World!';
console.log(greeting);
\`\`\`

> Start editing to see the magic!
`;

const initialSource = loadSource() || DEFAULT_SOURCE;

export const useEditorStore = create<EditorState>()(
  devtools(
    (set, get) => ({
      source: initialSource,
      renderedHtml: '',
      liveSync: true,
      viewMode: 'standard' as ViewMode,
      detectedFormat: 'md' as DetectedFormat,
      pendingSource: null,
      lastSource: null,
      isRefreshing: false,

      setSource: (source: string) => {
        set({ source });
        saveSource(source);
        log.state('setSource', { length: source.length });
      },

      setRenderedHtml: (html: string) => {
        set({ renderedHtml: html });
      },

      toggleLiveSync: () => {
        const { liveSync, source, pendingSource } = get();
        if (!liveSync && pendingSource) {
          set({ liveSync: true, source: pendingSource, pendingSource: null });
        } else {
          set({ liveSync: !liveSync, pendingSource: liveSync ? source : null });
        }
        log.state('toggleLiveSync', { liveSync: !liveSync });
      },

      setViewMode: (viewMode: ViewMode) => {
        set({ viewMode });
        log.state('setViewMode', { viewMode });
      },

      hardRefresh: () => {
        set({ isRefreshing: true });
        setTimeout(() => set({ isRefreshing: false }), 300);
        log.state('hardRefresh', {});
      },

      cleanSlate: () => {
        const { source } = get();
        set({ lastSource: source, source: '', renderedHtml: '' });
        saveSource('');
        log.state('cleanSlate', { savedLength: source.length });
      },

      undoClean: () => {
        const { lastSource } = get();
        if (lastSource) {
          set({ source: lastSource, lastSource: null });
          saveSource(lastSource);
          log.state('undoClean', { restoredLength: lastSource.length });
        }
      },

      setIsRefreshing: (isRefreshing: boolean) => {
        set({ isRefreshing });
      },
    }),
    { name: 'html2html-Store' }
  )
);

if (import.meta.env.DEV) {
  (window as unknown as { __HTML2HTML_STORE__: typeof useEditorStore }).
    __HTML2HTML_STORE__ = useEditorStore;
}
